import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, Download, Loader2, Phone, Clock3, PackageCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { BIZ } from "@/constants/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMELINE = ["pending_approval", "approved", "label_sent", "shipped", "delivered"];
const STEP_LABELS = {
  pending_approval: "Request received — pending approval",
  approved: "Label approved",
  label_sent: "Prepaid label sent to your email",
  shipped: "Package in transit to JSL",
  delivered: "Delivered to our repair facility",
};
const REJECT_NOTE = "This request was not approved — please call us to sort it out.";

export default function TrackLabel() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/label-request/track?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Could not load your request.");
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [token]);

  const status = data?.status || "";
  const idx = TIMELINE.indexOf(status);
  const rejected = status === "rejected" || status === "cancelled";

  return (
    <div className="blueprint flex min-h-screen items-center justify-center bg-paper p-4">
      <div className="w-full max-w-xl rounded-[24px] border border-ink/10 bg-white p-8 shadow-[0_30px_80px_rgba(22,50,79,0.15)] sm:p-10" data-testid="track-card">
        <div className="flex items-center justify-between gap-3">
          <Logo />
          <a href="tel:+17162566630" className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-[12.5px] font-bold text-ink" data-testid="track-call-link">
            <Phone className="h-3.5 w-3.5 text-brandsky-deep" /> (716) 256-6630
          </a>
        </div>

        {!token && (
          <p className="mt-8 text-[14px] text-slate-500" data-testid="track-no-token">
            Open this page from the secure link in your email — or call us and we'll check your request right away.
          </p>
        )}
        {error && <p className="mt-8 text-[14px] font-semibold text-brandpink" data-testid="track-error">{error}</p>}
        {!data && !error && token && (
          <p className="mt-8 flex items-center gap-2 text-[14px] text-slate-500" data-testid="track-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your request…
          </p>
        )}

        {data && (
          <div className="mt-8" data-testid="track-status-panel">
            <p className="eyebrow text-slate-400">Request {data.request_id}</p>
            <h1 className="font-display mt-2 text-2xl font-extrabold text-ink">
              {rejected ? REJECT_NOTE : "Your mail-in repair shipment"}
            </h1>

            <ol className="mt-7 space-y-0">
              {TIMELINE.map((s, i) => {
                const reached = !rejected && idx >= i;
                const current = !rejected && idx === i;
                const last = i === TIMELINE.length - 1;
                return (
                  <li key={s} className="relative flex gap-4 pb-7 last:pb-0" data-testid={`track-step-${s}`}>
                    {!last && <span className={`absolute left-[11px] top-6 h-full w-0.5 ${reached ? "bg-brandleaf" : "bg-ink/10"}`} />}
                    <span className={`relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                      reached ? "bg-brandleaf text-white" : "border-2 border-ink/15 bg-white text-slate-300"
                    }`}>
                      {reached ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-2 w-2" />}
                    </span>
                    <div>
                      <p className={`text-[14.5px] font-bold ${current ? "text-ink" : reached ? "text-ink" : "text-slate-400"}`}>
                        {STEP_LABELS[s]}
                        {current && <span className="ml-2 rounded-full bg-brandsky-soft px-2 py-0.5 text-[10.5px] font-extrabold uppercase tracking-wide text-brandsky-deep">current</span>}
                      </p>
                      {s === "delivered" && reached && (
                        <p className="mt-1 text-[12.5px] text-slate-500">Your handpieces still need to be checked in — we'll be in touch with a repair estimate.</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            {data.label?.tracking && (
              <p className="mt-2 rounded-2xl border border-ink/10 bg-paper px-4 py-3 text-[13.5px]" data-testid="track-tracking-number">
                <strong className="text-ink">USPS tracking:</strong>{" "}
                <span className="font-mono font-bold text-brandsky-deep">{data.label.tracking}</span>
                {" · "}
                <a href={`https://m.usps.com/m/TrackConfirmAction?label=${data.label.tracking}`} target="_blank" rel="noreferrer" className="font-semibold text-brandsky-deep link-underline">
                  view on usps.com
                </a>
              </p>
            )}

            {data.label?.download_url && (
              <motion.a
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                href={`${API}${data.label.download_url}`}
                data-testid="track-download-button"
                className="btn-lift mt-5 flex items-center justify-center gap-2.5 rounded-full bg-ink px-6 py-3.5 text-[14px] font-bold text-white hover:bg-abyss"
              >
                <Download className="h-4 w-4" /> Download my shipping label (PDF)
              </motion.a>
            )}

            <p className="mt-6 flex items-start gap-2 text-[12px] leading-relaxed text-slate-400">
              <Clock3 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Once we get the handpieces in, it's 24–72 hours until they're shipped back to the office.
              Questions? Call Jacob or Jonah any time.
            </p>
          </div>
        )}

        <p className="mt-8 flex items-center gap-2 text-[11.5px] text-slate-400">
          <PackageCheck className="h-3.5 w-3.5" /> This page is a secure link tied to your email — only someone with the link can see your request.
        </p>
      </div>
    </div>
  );
}